import { useEffect, useRef, useState } from "react";
import { Draggable, Droppable } from "@hello-pangea/dnd";
import { useTranslation } from "react-i18next";
import { useAppSelector, useAppDispatch } from "hooks";
import {
  setModalxComponent,
  setModalxComponentProps,
} from "components/modalx/modalx.slice";
import { addCardThunk } from "containers/project-board/project-board-container.slice";
import { prefersReducedMotion } from "features/project-board/project-motion";
import ProjectCard from "./ProjectCard";
import AddCardComposer from "./AddCardComposer";
import MoveCardSheet from "./MoveCardSheet";

const EMPTY: string[] = [];

interface BoardColumnProps {
  columnId: string;
  index: number;
  companyId: string;
  boardId: string;
  onOpenCard: (cardId: string) => void;
  /** Brief highlight after a card is dropped into this column. */
  flash?: boolean;
}

export default function BoardColumn({
  columnId,
  index,
  companyId,
  boardId,
  onOpenCard,
  flash = false,
}: BoardColumnProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const column = useAppSelector(
    (s) => s.projectBoardReducer.columnsById[columnId]
  );
  const cardIds = useAppSelector(
    (s) => s.projectBoardReducer.cardIdsByColumn[columnId] || EMPTY
  );

  const [countBounce, setCountBounce] = useState(false);
  const [movingCardId, setMovingCardId] = useState<string | null>(null);
  const prevCount = useRef(cardIds.length);

  useEffect(() => {
    if (prevCount.current === cardIds.length) return;
    prevCount.current = cardIds.length;
    if (prefersReducedMotion()) return;
    setCountBounce(true);
    const timer = window.setTimeout(() => setCountBounce(false), 200);
    return () => window.clearTimeout(timer);
  }, [cardIds.length]);

  if (!column) return null;

  const canAddCards = !column.__optimistic && !columnId.startsWith("temp-col");

  const openColumnMenu = () => {
    dispatch(setModalxComponent("ProjectBoardColumnForm"));
    dispatch(
      setModalxComponentProps({ projectBoardId: boardId, id: columnId })
    );
  };

  return (
    <Draggable draggableId={columnId} index={index}>
      {(colProvided) => (
        <div
          ref={colProvided.innerRef}
          {...colProvided.draggableProps}
          className={`flex max-h-full w-72 shrink-0 flex-col rounded-xl bg-[var(--p-color-bg-surface-secondary)] shadow-sm transition-colors duration-300 ${
            flash ? "bg-[var(--p-color-bg-surface-brand)]" : ""
          }`}
        >
          {/* Header */}
          <div className="flex items-center gap-2 px-3 py-2.5">
            <div
              {...colProvided.dragHandleProps}
              className="flex min-w-0 flex-1 items-center gap-2"
            >
              <h3 className="flex-1 truncate text-[14px] font-semibold text-[var(--p-color-text)]">
                {column.name}
              </h3>
              <span
                className={`rounded bg-[var(--p-color-bg-surface-tertiary)] px-1.5 py-0.5 text-[11px] font-medium text-[var(--p-color-text-secondary)] ${
                  countBounce ? "scale-115 animate-none" : ""
                }`}
                style={
                  countBounce && !prefersReducedMotion()
                    ? {
                        transform: "scale(1.15)",
                        transition: "transform 200ms ease",
                      }
                    : { transition: "transform 200ms ease" }
                }
              >
                {cardIds.length}
              </span>
            </div>
            {canAddCards && (
              <button
                type="button"
                onClick={openColumnMenu}
                aria-label={t("edit", { defaultValue: "Edit list" })}
                className="rounded p-1 text-[var(--p-color-text-secondary)] hover:bg-[var(--p-color-bg-surface-tertiary)] hover:text-[var(--p-color-text)] max-md:-my-2 max-md:-mr-2 max-md:flex max-md:h-11 max-md:w-11 max-md:items-center max-md:justify-center"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <circle cx="4" cy="10" r="1.6" />
                  <circle cx="10" cy="10" r="1.6" />
                  <circle cx="16" cy="10" r="1.6" />
                </svg>
              </button>
            )}
          </div>

          {/* Cards */}
          <Droppable droppableId={columnId} type="CARD">
            {(dropProvided, dropSnapshot) => (
              <div
                ref={dropProvided.innerRef}
                {...dropProvided.droppableProps}
                className={`flex-1 overflow-y-auto px-2 [scrollbar-width:thin] [scrollbar-color:rgba(0,0,0,0.22)_transparent] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-black/20 hover:[&::-webkit-scrollbar-thumb]:bg-black/30 [&::-webkit-scrollbar-track]:bg-transparent ${
                  dropSnapshot.isDraggingOver
                    ? "bg-[var(--p-color-bg-surface-tertiary)]"
                    : ""
                }`}
                style={{ minHeight: 8 }}
              >
                {cardIds.map((cardId, i) => (
                  <Draggable key={cardId} draggableId={cardId} index={i}>
                    {(cardProvided, cardSnapshot) => (
                      <div
                        ref={cardProvided.innerRef}
                        {...cardProvided.draggableProps}
                        {...cardProvided.dragHandleProps}
                        className="mb-2"
                      >
                        <ProjectCard
                          cardId={cardId}
                          isDragging={cardSnapshot.isDragging}
                          onOpen={() => onOpenCard(cardId)}
                          onMove={() => setMovingCardId(cardId)}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {dropProvided.placeholder}
              </div>
            )}
          </Droppable>

          {/* Footer */}
          <div className="px-2 pb-2">
            {canAddCards ? (
              <AddCardComposer
                onAdd={(name) =>
                  dispatch(
                    addCardThunk({ companyId, boardId, columnId, name })
                  )
                }
              />
            ) : null}
          </div>

          {movingCardId && (
            <MoveCardSheet
              cardId={movingCardId}
              companyId={companyId}
              onClose={() => setMovingCardId(null)}
            />
          )}
        </div>
      )}
    </Draggable>
  );
}
